import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { DisplayAreaList } from '../../../models/display-area.model';
import { DisplayAreaService } from '../../../services/display-area.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-display-area-list',

  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    BreadcrumbModule,
    ToolbarModule,
    ButtonModule,
    RouterLink,
    ProgressSpinnerModule,
    ToastModule,
    ToggleSwitchModule,
    ConfirmDialogModule,
    TagStatusComponent,
  ],
  templateUrl: './display-area-list.component.html',

  providers: [ConfirmationService, MessageService],
})
export class DisplayAreaListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private displayAreaService = inject(DisplayAreaService);
  private messageService = inject(MessageService);
  paginationService = inject(PaginationService);

  displayAreas = signal<DisplayAreaList[]>([]);

  pageNumber = signal(1);
  pageSize = signal(10);
  totalRecords = signal(0);
  totalPages = signal(0);

  loading = signal(false);

  nameFilter?: string;
  slugFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize(),
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.displayAreaService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.nameFilter,
        this.slugFilter,
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.displayAreas.set(page.items as DisplayAreaList[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar as áreas de exibição',
              life: 3000,
            });
          }
        },
      });
  }

  getItemTypeDescription(type: string): string {
    switch (type) {
      case 'Product':
        return 'Produto';
      case 'Model':
        return 'Modelo';
      case 'ModelVersion':
        return 'Versão do Modelo';
      case 'Category':
        return 'Categoria';
      case 'Brand':
        return 'Marca';
      default:
        return 'Desconhecido';
    }
  }

  getStatusDescription(isActive: boolean): string {
    return isActive ? 'Ativo' : 'Inativo';
  }
}
